import { useContext, useMemo } from 'react'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { getBnblPackageViews } from 'selectors/offerPage/offerPageSelectors'
import { isLEHotel } from 'lib/offer/offerTypes'
import { requiresFlashMultiRoomAssistance } from 'lib/offer/flashPurchasePolicy'
import { isBuyNowBookLaterAvailableForPackage } from 'components/OfferPage/BnblModal/utils'
import useLoyaltyOfferPageTargetStayUpgrade from 'components/OfferPage/hooks/useLoyaltyOfferPageTargetStayUpgrade'

interface Input {
  offer: App.HotelOffer
  packageView?: App.OfferPackageOptionView
  roomCount: number
  checkIn?: string
  canRedeemLuxPlusBenefits: boolean
  baseBnblPackageViews: ReadonlyArray<App.OfferPackageOptionView>
  charterBundleRoute?: App.CharterBundleRoute
  upgradeApplied: boolean
}

export function getFlashPackageSelectionCapabilities({ offer, packageView, roomCount, checkIn, canRedeemLuxPlusBenefits, baseBnblPackageViews, charterBundleRoute, upgradeApplied }: Input) {
  const requiresAssistance = requiresFlashMultiRoomAssistance({ roomCount, canRedeemLuxPlusBenefits, hotelMemberTotal: packageView?.hotelMemberTotal ?? 0 })
  return {
    canAddToCart: !requiresAssistance && !!checkIn,
    canBuyNowBookLater: !upgradeApplied && !charterBundleRoute && !requiresAssistance && !!packageView &&
      baseBnblPackageViews.some(base => base.package.uniqueKey === packageView.package.uniqueKey) &&
      isBuyNowBookLaterAvailableForPackage({ capacityValid: packageView.capacityValid, hasBuyNowBookLater: offer.hasBuyNowBookLater, allowBuyNowBookLater: packageView.package.allowBuyNowBookLater }),
  }
}

const NO_ACTIONS = { canAddToCart: false, canBuyNowBookLater: false }

/** Share the data and capabilities used by D owners to decide whether to mount purchase options. */
export default function useFlashPackageSelectionCapabilities(packageView?: App.OfferPackageOptionView) {
  const state = useContext(OfferPageStateContext)
  const { offer, rooms, checkInDate: checkIn, charterBundleRoute } = state
  const canRedeemLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)
  const baseBnblPackageViews = getBnblPackageViews(state)
  const { selectedTargetUpgradePackage } = useLoyaltyOfferPageTargetStayUpgrade(packageView?.package)
  return useMemo(() => isLEHotel(offer) ? getFlashPackageSelectionCapabilities({ offer, packageView, roomCount: rooms.length, checkIn, canRedeemLuxPlusBenefits, baseBnblPackageViews, charterBundleRoute, upgradeApplied: !!selectedTargetUpgradePackage }) : NO_ACTIONS,
    [baseBnblPackageViews, canRedeemLuxPlusBenefits, charterBundleRoute, checkIn, offer, packageView, rooms.length, selectedTargetUpgradePackage])
}
